/**
 * An error whose message is written for the person who triggered it, not for a
 * developer — a field they need to fix, an action they need to retry. Two
 * things follow from throwing one:
 *
 * - the UI may show `message` verbatim, so it must read as advice to a user;
 * - reporting policy skips it. An expected, user-correctable outcome is not a
 *   defect, and filing one as a production error buries the real ones. DCM-SV
 *   was exactly that: "Please complete your name and email above before
 *   uploading a photo." raised as a Sentry issue.
 *
 * Wrap a genuine failure in one only after it has been reported (directly, or
 * centrally via `reportMutationError`), passing the original as `cause`.
 *
 * One thrown by a server function reaches the browser still a
 * `UserFacingError` (see `user-facing-error-adapter.ts`), so `instanceof`
 * holds on either side of the wire.
 *
 * Pure utility — safe to import from server or client code.
 */
export class UserFacingError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "UserFacingError";
  }
}

/**
 * A refusal about one record: it does not exist (`not_found`), or its state
 * no longer allows the action (`conflict`), such as a request someone already
 * decided. Callers that need the reason, like the admin MCP tools, read
 * `code`; pages show `message` as for any `UserFacingError`.
 */
export class RecordStateError extends UserFacingError {
  readonly code: "not_found" | "conflict";

  constructor(code: "not_found" | "conflict", message: string) {
    super(message);
    this.name = "RecordStateError";
    this.code = code;
  }
}
